// Static facts about every procedural texture: where it makes sense, how large one repeat is in the
// room, and whether the accent color does anything. The design panel filters its texture picker by
// `surfaces`; the renderer derives repeat counts from `naturalSize`. Pure, no runtime imports.
import type { SurfaceId, SurfaceMaterial, TextureId } from './room-design.ts';

export type TextureInfo = {
    surfaces: SurfaceId[];
    /** Meters covered by one repeat at scale 1. */
    naturalSize: number;
    /** Whether `accent` changes the picture. */
    hasAccent: boolean;
    /** Bump strength for the painted height field; 0 = no relief map. */
    relief: number;
};

const ALL: SurfaceId[] = ['walls', 'floor', 'ceiling', 'curtain', 'seats', 'stage', 'seatShell'];

export const TEXTURE_CATALOG: Record<TextureId, TextureInfo> = {
    plain: { surfaces: ALL, naturalSize: 1, hasAccent: false, relief: 0 },
    velvet: { surfaces: ['curtain', 'seats', 'walls', 'seatShell'], naturalSize: 0.6, hasAccent: false, relief: 0.35 },
    'fabric-weave': {
        surfaces: ['seats', 'walls', 'curtain', 'seatShell'],
        naturalSize: 0.32,
        hasAccent: false,
        relief: 0.6
    },
    leather: { surfaces: ['seats', 'walls', 'stage', 'seatShell'], naturalSize: 0.7, hasAccent: true, relief: 0.6 },
    // Diamond-stitched leather; the accent is the thread.
    quilted: { surfaces: ['seats', 'walls', 'seatShell'], naturalSize: 0.5, hasAccent: true, relief: 0.9 },
    'carpet-dots': { surfaces: ['floor', 'stage'], naturalSize: 1, hasAccent: true, relief: 0.4 },
    'carpet-stars': { surfaces: ['floor', 'stage'], naturalSize: 1.2, hasAccent: true, relief: 0.4 },
    'carpet-waves': { surfaces: ['floor', 'stage'], naturalSize: 1.2, hasAccent: true, relief: 0.35 },
    'wood-panels': {
        surfaces: ['walls', 'floor', 'stage', 'ceiling', 'seats', 'seatShell'],
        naturalSize: 1.2,
        hasAccent: true,
        relief: 0.8
    },
    parquet: { surfaces: ['floor', 'stage'], naturalSize: 1.2, hasAccent: true, relief: 0.5 },
    concrete: { surfaces: ['floor', 'walls', 'ceiling', 'stage'], naturalSize: 2, hasAccent: true, relief: 0.7 },
    'acoustic-panels': { surfaces: ['walls', 'ceiling'], naturalSize: 1.6, hasAccent: true, relief: 1 },
    'wood-slats': { surfaces: ['walls', 'ceiling', 'stage'], naturalSize: 1.2, hasAccent: true, relief: 0.9 },
    brick: { surfaces: ['walls'], naturalSize: 1, hasAccent: true, relief: 0.9 },
    marble: { surfaces: ['floor', 'walls', 'stage'], naturalSize: 2, hasAccent: true, relief: 0 },
    'art-deco': { surfaces: ['walls', 'ceiling', 'curtain'], naturalSize: 1.6, hasAccent: true, relief: 0.7 },
    starfield: { surfaces: ['ceiling', 'walls'], naturalSize: 3, hasAccent: true, relief: 0 },
    image: { surfaces: ALL, naturalSize: 2, hasAccent: false, relief: 0 }
};

/**
 * Tile repeats for a surface `widthM` x `heightM` meters large: one repeat per `naturalSize`, times
 * the surface's own `scale` (a larger scale means a finer pattern).
 */
export function surfaceRepeat(
    material: Pick<SurfaceMaterial, 'texture' | 'scale'>,
    widthM: number,
    heightM: number
): [number, number] {
    const natural = TEXTURE_CATALOG[material.texture].naturalSize;
    const k = material.scale / natural;
    return [Math.max(0.05, widthM * k), Math.max(0.05, heightM * k)];
}
